import { useState, useEffect } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Separator } from '@/components/ui/separator';
import { useToast } from '@/hooks/use-toast';
import { Building, User, Shield, Bell, Trash2, Lock, Monitor, Save, Upload, X, UserPlus, Table as TableIcon, MapPin, Network } from 'lucide-react';
import { Checkbox } from '@/components/ui/checkbox';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { AVAILABLE_COLUMNS, COLUMN_PRESETS, DEFAULT_COLUMNS } from '@/config/assetsTableColumns';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { SnapshotManager } from '@/components/snapshots/SnapshotManager';
import { InviteUserDialog } from '@/components/settings/InviteUserDialog';
import { PendingInvitations } from '@/components/settings/PendingInvitations';

export default function SettingsPage() {
  const { organization, profile } = useAuth();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Organization settings state
  const [orgName, setOrgName] = useState(organization?.name || '');
  const [orgCountry, setOrgCountry] = useState(organization?.country || 'SE');
  const [orgLanguage, setOrgLanguage] = useState(organization?.language || 'sv-SE');
  const [orgTimezone, setOrgTimezone] = useState(organization?.timezone || 'Europe/Stockholm');
  const [logoUrl, setLogoUrl] = useState(organization?.logo_url || '');
  const [logoLink, setLogoLink] = useState(organization?.logo_link || '');
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [inviteDialogOpen, setInviteDialogOpen] = useState(false);

  // Sync state with organization data when it changes
  useEffect(() => {
    if (organization) {
      setOrgName(organization.name || '');
      setOrgCountry(organization.country || 'SE');
      setOrgLanguage(organization.language || 'sv-SE');
      setOrgTimezone(organization.timezone || 'Europe/Stockholm');
      setLogoUrl(organization.logo_url || '');
      setLogoLink(organization.logo_link || '');
    }
  }, [organization]);

  // Profile settings state
  const [profileName, setProfileName] = useState(profile?.name || '');

  const { data: profiles } = useQuery({
    queryKey: ['org-profiles', organization?.id],
    queryFn: async () => {
      if (!organization?.id || profile?.role !== 'admin') return [];
      
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('org_id', organization.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      return data || [];
    },
    enabled: !!organization?.id && profile?.role === 'admin',
  });

  const updateOrganizationMutation = useMutation({
    mutationFn: async (data: { name: string; country: string; language: string; timezone: string; logo_url?: string | null; logo_link?: string | null }) => {
      if (!organization?.id) throw new Error('No organization found');
      
      const { error } = await supabase
        .from('orgs')
        .update(data)
        .eq('id', organization.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Organization updated',
        description: 'Organization settings have been saved successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update organization settings.',
        variant: 'danger',
      });
    },
  });

  const updateProfileMutation = useMutation({
    mutationFn: async (data: { name: string }) => {
      if (!profile?.id) throw new Error('No profile found');
      
      const { error } = await supabase
        .from('profiles')
        .update(data)
        .eq('id', profile.id);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: 'Profile updated',
        description: 'Your profile has been updated successfully.',
      });
      queryClient.invalidateQueries({ queryKey: ['profile'] });
    },
    onError: () => {
      toast({
        title: 'Error',
        description: 'Failed to update profile.',
        variant: 'danger',
      });
    },
  });

  const deleteOrganizationMutation = useMutation({
    mutationFn: async () => {
      if (!organization?.id) throw new Error('No organization found');
      if (profile?.role !== 'admin') throw new Error('Only admins can delete organization');
      
      // Call edge function to delete org and user account
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No session found');

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const response = await fetch(
        `${supabaseUrl}/functions/v1/delete-organization`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${session.access_token}`,
            'Content-Type': 'application/json',
          },
        }
      );

      const result = await response.json();
      
      if (!response.ok) {
        throw new Error(result.error || 'Failed to delete organization');
      }

      return result;
    },
    onSuccess: async () => {
      toast({
        title: 'Organization deleted',
        description: 'Your organization and account have been permanently deleted.',
      });
      
      // Clear local storage and redirect
      await supabase.auth.signOut();
      window.location.href = '/';
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete organization.',
        variant: 'danger',
      });
    },
  });

  const handleUpdateOrganization = () => {
    updateOrganizationMutation.mutate({
      name: orgName,
      country: orgCountry,
      language: orgLanguage,
      timezone: orgTimezone,
      logo_url: logoUrl || null,
      logo_link: logoLink || null,
    });
  };

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !organization?.id) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      toast({
        title: 'Invalid file',
        description: 'Please upload an image file',
        variant: 'danger',
      });
      return;
    }

    // Validate file size (max 2MB)
    if (file.size > 2 * 1024 * 1024) {
      toast({
        title: 'File too large',
        description: 'Logo must be less than 2MB',
        variant: 'danger',
      });
      return;
    }

    setUploadingLogo(true);

    try {
      // Delete old logo if exists
      if (logoUrl) {
        const oldPath = logoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('logos')
            .remove([`${organization.id}/${oldPath}`]);
        }
      }

      // Upload new logo
      const fileExt = file.name.split('.').pop();
      const fileName = `${Date.now()}.${fileExt}`;
      const filePath = `${organization.id}/${fileName}`;

      const { error: uploadError } = await supabase.storage
        .from('logos')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('logos')
        .getPublicUrl(filePath);

      setLogoUrl(publicUrl);

      // Automatically save to database
      const { error: updateError } = await supabase
        .from('orgs')
        .update({ logo_url: publicUrl })
        .eq('id', organization.id);

      if (updateError) throw updateError;

      // Invalidate queries to refresh organization data
      queryClient.invalidateQueries({ queryKey: ['organization'] });

      toast({
        title: 'Logo uploaded successfully',
        description: 'Your organization logo has been updated.',
      });
    } catch (error) {
      console.error('Error uploading logo:', error);
      toast({
        title: 'Upload failed',
        description: 'Failed to upload logo',
        variant: 'danger',
      });
    } finally {
      setUploadingLogo(false);
    }
  };

  const handleRemoveLogo = async () => {
    if (!organization?.id) return;

    try {
      // Delete from storage
      if (logoUrl) {
        const oldPath = logoUrl.split('/').pop();
        if (oldPath) {
          await supabase.storage
            .from('logos')
            .remove([`${organization.id}/${oldPath}`]);
        }
      }

      // Update database
      const { error } = await supabase
        .from('orgs')
        .update({ logo_url: null })
        .eq('id', organization.id);

      if (error) throw error;

      setLogoUrl('');
      
      // Invalidate queries to refresh organization data
      queryClient.invalidateQueries({ queryKey: ['organization'] });

      toast({
        title: 'Logo removed',
        description: 'Your organization logo has been removed.',
      });
    } catch (error) {
      console.error('Error removing logo:', error);
      toast({
        title: 'Error',
        description: 'Failed to remove logo.',
        variant: 'danger',
      });
    }
  };

  const handleUpdateProfile = () => {
    updateProfileMutation.mutate({
      name: profileName,
    });
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-3xl font-bold">Settings</h1>
        <p className="text-muted-foreground">Manage your organization and account settings</p>
      </div>

      <Tabs defaultValue="organization" className="space-y-4">
        <TabsList>
          <TabsTrigger value="organization" className="gap-2">
            <Building className="h-4 w-4" />
            Organization
          </TabsTrigger>
          <TabsTrigger value="profile" className="gap-2">
            <User className="h-4 w-4" />
            Profile
          </TabsTrigger>
          {profile?.role === 'admin' && (
            <TabsTrigger value="users" className="gap-2">
              <Shield className="h-4 w-4" />
              Users
            </TabsTrigger>
          )}
          <TabsTrigger value="notifications" className="gap-2">
            <Bell className="h-4 w-4" />
            Notifications
          </TabsTrigger>
          <TabsTrigger value="privacy" className="gap-2">
            <Lock className="h-4 w-4" />
            Privacy & Data
          </TabsTrigger>
          <TabsTrigger value="interface" className="gap-2">
            <Monitor className="h-4 w-4" />
            Interface
          </TabsTrigger>
          <TabsTrigger value="backups" className="gap-2">
            <Save className="h-4 w-4" />
            Storage
          </TabsTrigger>
        </TabsList>

        <TabsContent value="organization" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Organization Details</CardTitle>
              <CardDescription>
                Update your organization's basic information and preferences
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label>Organization Logo</Label>
                  <div className="flex items-center gap-4">
                    {logoUrl ? (
                      <div className="relative">
                        <img src={logoUrl} alt="Organization logo" className="h-16 w-auto max-w-[200px] object-contain" />
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          className="absolute -top-2 -right-2 h-6 w-6"
                          onClick={handleRemoveLogo}
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      </div>
                    ) : (
                      <div className="h-16 w-32 border-2 border-dashed rounded flex items-center justify-center text-muted-foreground text-sm">
                        No logo
                      </div>
                    )}
                    <div className="flex flex-col gap-2">
                      <Input
                        type="file"
                        id="logo-upload"
                        accept="image/*"
                        className="hidden"
                        onChange={handleLogoUpload}
                        disabled={uploadingLogo}
                      />
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={() => document.getElementById('logo-upload')?.click()}
                        disabled={uploadingLogo}
                      >
                        <Upload className="h-4 w-4 mr-2" />
                        {uploadingLogo ? 'Uploading...' : 'Upload Logo'}
                      </Button>
                      <p className="text-xs text-muted-foreground">PNG, JPG up to 2MB</p>
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="logo-link">Logo Link (Optional)</Label>
                  <Input
                    id="logo-link"
                    value={logoLink}
                    onChange={(e) => setLogoLink(e.target.value)}
                    placeholder="https://yourcompany.com"
                  />
                  <p className="text-xs text-muted-foreground">
                    URL to navigate to when logo is clicked
                  </p>
                </div>
              </div>

              <Separator />

              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="org-name">Organization Name</Label>
                  <Input
                    id="org-name"
                    value={orgName}
                    onChange={(e) => setOrgName(e.target.value)}
                    placeholder="Enter organization name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-country">Country</Label>
                  <Select value={orgCountry} onValueChange={setOrgCountry}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select country" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="SE">Sweden</SelectItem>
                      <SelectItem value="NO">Norway</SelectItem>
                      <SelectItem value="DK">Denmark</SelectItem>
                      <SelectItem value="FI">Finland</SelectItem>
                      <SelectItem value="DE">Germany</SelectItem>
                      <SelectItem value="US">United States</SelectItem>
                      <SelectItem value="GB">United Kingdom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-language">Language</Label>
                  <Select value={orgLanguage} onValueChange={setOrgLanguage}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select language" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="sv-SE">Swedish</SelectItem>
                      <SelectItem value="en-US">English (US)</SelectItem>
                      <SelectItem value="en-GB">English (UK)</SelectItem>
                      <SelectItem value="no-NO">Norwegian</SelectItem>
                      <SelectItem value="da-DK">Danish</SelectItem>
                      <SelectItem value="fi-FI">Finnish</SelectItem>
                      <SelectItem value="de-DE">German</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="org-timezone">Timezone</Label>
                  <Select value={orgTimezone} onValueChange={setOrgTimezone}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select timezone" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Europe/Stockholm">Europe/Stockholm</SelectItem>
                      <SelectItem value="Europe/Oslo">Europe/Oslo</SelectItem>
                      <SelectItem value="Europe/Copenhagen">Europe/Copenhagen</SelectItem>
                      <SelectItem value="Europe/Helsinki">Europe/Helsinki</SelectItem>
                      <SelectItem value="Europe/Berlin">Europe/Berlin</SelectItem>
                      <SelectItem value="Europe/London">Europe/London</SelectItem>
                      <SelectItem value="America/New_York">America/New_York</SelectItem>
                      <SelectItem value="America/Los_Angeles">America/Los_Angeles</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleUpdateOrganization}
                  disabled={updateOrganizationMutation.isPending}
                >
                  {updateOrganizationMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>

          {profile?.role === 'admin' && (
            <Card className="border-danger">
              <CardHeader>
                <CardTitle className="text-danger">Danger Zone</CardTitle>
                <CardDescription>
                  Permanently delete this organization and all associated data
                </CardDescription>
              </CardHeader>
              <CardContent>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button variant="danger" className="w-full sm:w-auto">
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete Organization
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Are you absolutely sure?</AlertDialogTitle>
                      <AlertDialogDescription>
                        This action cannot be undone. This will permanently delete your
                        organization "{organization?.name}" and remove all associated data including:
                        <ul className="list-disc list-inside mt-2 space-y-1">
                          <li>All sites and zones</li>
                          <li>All assets and systems</li>
                          <li>All risks and controls</li>
                          <li>All evidence and documents</li>
                          <li>All user access and roles</li>
                        </ul>
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={() => deleteOrganizationMutation.mutate()}
                        className="bg-danger hover:bg-danger/90 text-danger-foreground"
                      >
                        {deleteOrganizationMutation.isPending ? 'Deleting...' : 'Delete Organization'}
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="profile" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Profile Information</CardTitle>
              <CardDescription>
                Update your personal profile information
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-4 md:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="profile-name">Display Name</Label>
                  <Input
                    id="profile-name"
                    value={profileName}
                    onChange={(e) => setProfileName(e.target.value)}
                    placeholder="Enter your name"
                  />
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-email">Email</Label>
                  <Input
                    id="profile-email"
                    value={profile?.email || ''}
                    disabled
                    className="bg-muted"
                  />
                  <p className="text-xs text-muted-foreground">
                    Email cannot be changed from here
                  </p>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="profile-role">Role</Label>
                  <Input
                    id="profile-role"
                    value={profile?.role || ''}
                    disabled
                    className="bg-muted capitalize"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <Button 
                  onClick={handleUpdateProfile}
                  disabled={updateProfileMutation.isPending}
                >
                  {updateProfileMutation.isPending ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {profile?.role === 'admin' && (
          <TabsContent value="users" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>User Management</CardTitle>
                <CardDescription>
                  Manage users and their access to your organization
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-3">
                    <h4 className="text-sm font-medium text-muted-foreground">Current Members</h4>
                    {profiles?.map((user) => (
                      <div key={user.id} className="flex items-center justify-between p-4 border rounded-lg">
                        <div>
                          <p className="font-medium">{user.name || user.email}</p>
                          <p className="text-sm text-muted-foreground">{user.email}</p>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {user.role}
                          </Badge>
                          {user.user_id !== profile?.user_id && (
                            <Button variant="ghost" size="sm" className="text-danger">
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  <Separator />
                  
                  <PendingInvitations />
                  
                  <Separator />
                  
                  <Button className="w-full" onClick={() => setInviteDialogOpen(true)}>
                    <UserPlus className="h-4 w-4 mr-2" />
                    Invite User
                  </Button>
                </div>
              </CardContent>
            </Card>
            
            <InviteUserDialog 
              open={inviteDialogOpen} 
              onOpenChange={setInviteDialogOpen} 
            />
          </TabsContent>
        )}

        <TabsContent value="notifications" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Notification Preferences</CardTitle>
              <CardDescription>
                Configure how you want to receive notifications
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-6">
                <div className="flex items-center justify-between">
                  <div className="space-y-0.5">
                    <Label>Show my own activities</Label>
                    <p className="text-sm text-muted-foreground">
                      Display your own changes in the activity feed
                    </p>
                  </div>
                  <Switch
                    checked={profile?.preferences?.show_own_activities !== false}
                    onCheckedChange={(checked) => {
                      const newPreferences = {
                        ...profile?.preferences,
                        show_own_activities: checked,
                      };
                      updateProfileMutation.mutate({ preferences: newPreferences } as any);
                    }}
                  />
                </div>
                
                <Separator />
                
                <div className="space-y-2">
                  <Label>Real-time notifications</Label>
                  <p className="text-sm text-muted-foreground">
                    You'll receive toast notifications when other users make changes
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="privacy" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Privacy & Data Rights</CardTitle>
              <CardDescription>
                Manage your personal data according to GDPR
              </CardDescription>
            </CardHeader>
...
          </Card>
        </TabsContent>

        <TabsContent value="interface" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle>Assets Page Preferences</CardTitle>
              <CardDescription>
                Customize how the Assets page displays information by default
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Default View Selection */}
              <div className="space-y-2">
                <Label>Default View</Label>
                <Select 
                  value={profile?.preferences?.assets_default_view || 'layout'}
                  onValueChange={(value: 'table' | 'map' | 'layout') => {
                    const newPreferences = {
                      ...profile?.preferences,
                      assets_default_view: value,
                    };
                    updateProfileMutation.mutate({ preferences: newPreferences } as any);
                  }}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="table">
                      <div className="flex items-center gap-2">
                        <TableIcon className="h-4 w-4" />
                        <div>
                          <div>Table View</div>
                          <div className="text-xs text-muted-foreground">List all assets in a table</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="map">
                      <div className="flex items-center gap-2">
                        <MapPin className="h-4 w-4" />
                        <div>
                          <div>Map View</div>
                          <div className="text-xs text-muted-foreground">Show sites on a map</div>
                        </div>
                      </div>
                    </SelectItem>
                    <SelectItem value="layout">
                      <div className="flex items-center gap-2">
                        <Network className="h-4 w-4" />
                        <div>
                          <div>System Layout View</div>
                          <div className="text-xs text-muted-foreground">Visualize asset architecture</div>
                        </div>
                      </div>
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <Separator />

              {/* Table Columns Configuration */}
              <div className="space-y-4">
                <div>
                  <Label>Table View Columns</Label>
                  <p className="text-sm text-muted-foreground">
                    Select which columns to display in the table view
                  </p>
                </div>

                {/* Column Presets */}
                <div className="flex gap-2 flex-wrap">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPreferences = {
                        ...profile?.preferences,
                        assets_table_columns: COLUMN_PRESETS.minimal,
                      };
                      updateProfileMutation.mutate({ preferences: newPreferences } as any);
                    }}
                  >
                    Minimal
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPreferences = {
                        ...profile?.preferences,
                        assets_table_columns: COLUMN_PRESETS.standard,
                      };
                      updateProfileMutation.mutate({ preferences: newPreferences } as any);
                    }}
                  >
                    Standard
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPreferences = {
                        ...profile?.preferences,
                        assets_table_columns: COLUMN_PRESETS.detailed,
                      };
                      updateProfileMutation.mutate({ preferences: newPreferences } as any);
                    }}
                  >
                    Detailed
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      const newPreferences = {
                        ...profile?.preferences,
                        assets_table_columns: COLUMN_PRESETS.all,
                      };
                      updateProfileMutation.mutate({ preferences: newPreferences } as any);
                    }}
                  >
                    All Columns
                  </Button>
                </div>

                {/* Individual Column Checkboxes */}
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3 p-4 border rounded-lg">
                  {AVAILABLE_COLUMNS.map((column) => {
                    const selectedColumns = profile?.preferences?.assets_table_columns || DEFAULT_COLUMNS;
                    return (
                      <div key={column.key} className="flex items-center space-x-2">
                        <Checkbox
                          id={`column-${column.key}`}
                          checked={selectedColumns.includes(column.key)}
                          onCheckedChange={(checked) => {
                            const newColumns = checked
                              ? [...selectedColumns, column.key]
                              : selectedColumns.filter(c => c !== column.key);
                            const newPreferences = {
                              ...profile?.preferences,
                              assets_table_columns: newColumns,
                            };
                            updateProfileMutation.mutate({ preferences: newPreferences } as any);
                          }}
                        />
                        <Label htmlFor={`column-${column.key}`} className="text-sm cursor-pointer">
                          {column.label}
                        </Label>
                      </div>
                    );
                  })}
                </div>

                {/* Preview */}
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-xs font-medium mb-2">Preview:</p>
                  <div className="flex gap-2 flex-wrap">
                    {(profile?.preferences?.assets_table_columns || DEFAULT_COLUMNS).map(colKey => {
                      const col = AVAILABLE_COLUMNS.find(c => c.key === colKey);
                      return (
                        <Badge key={colKey} variant="secondary">
                          {col?.label}
                        </Badge>
                      );
                    })}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="backups">
          <SnapshotManager />
        </TabsContent>
      </Tabs>
    </div>
  );
}